/**
 * Recent workspaces — localStorage-backed list of the tenants this browser
 * has signed into recently. Powers the "Recent" pill row on the Login page
 * so returning users can one-click into their most-used workspace without
 * going through the multi-agency picker every time.
 *
 * Shape:
 *   [{ email, agency_id, agency_name, role, last_used_at }]
 *
 * Kept intentionally small (5 entries) and MRU-sorted. Dedupe key is
 * (email, agency_id). Corrupt / legacy entries are silently ignored so a
 * bad localStorage payload can never break the Login page.
 *
 * NOT synced across devices — that's a user-preference we could revisit
 * later if there's demand.
 */
const STORAGE_KEY = "iris.recent_workspaces_v1";
const MAX_ENTRIES = 5;

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e) => e && typeof e.email === "string" && typeof e.agency_id === "string",
    );
  } catch {
    return [];
  }
}

function safeWrite(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* localStorage disabled / full — non-fatal */
  }
}

/**
 * Record a successful sign-in. Called from AuthContext after login,
 * select-agency, and switch-agency all succeed. No-op for super_admin
 * (no agency_id) — cross-tenant identity doesn't need pinning to a row.
 */
export function recordRecentWorkspace({ email, agency_id, agency_name, role }) {
  if (!email || !agency_id) return;
  const next = [
    { email, agency_id, agency_name: agency_name || null, role: role || null, last_used_at: new Date().toISOString() },
    ...safeRead().filter((e) => !(e.email === email && e.agency_id === agency_id)),
  ];
  safeWrite(next);
}

/** Return the MRU list. Never throws. */
export function listRecentWorkspaces() {
  return safeRead();
}

/** Remove a single entry by (email, agency_id). */
export function forgetRecentWorkspace(email, agency_id) {
  const next = safeRead().filter((e) => !(e.email === email && e.agency_id === agency_id));
  safeWrite(next);
}

/** Drop the whole list — used by the "Clear" link in the pill row. */
export function clearRecentWorkspaces() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
